import type { CatalogEntry } from '../types';

/**
 * Saleshandy account sync (feature `20`) error codes + catalog.
 *
 * Code shape is `20AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * Endpoint → `AA` map: shared=00, link=01, unlink=02, consent=03, sync=04,
 * email-infra=05 (§8f, Phase 3 — Email-Infra/InboxKit-purchased mailboxes).
 *
 * All four `/saleshandy/*` routes sit behind `SaleshandyTokenGuard`'s static
 * shared secret — Saleshandy resolves the role-designated Owner and the
 * account's current plan on its own side and pushes both directly, so TI makes
 * no outbound call on the link/unlink/consent/sync paths (only the daily
 * reconciliation cron calls out). See docs/plans/saleshandy-account-sync.md.
 */
export const SALESHANDY_CODES = {
  /** Cross-endpoint conditions (shared=00). */
  Shared: {
    Unauthorized: '200001',
    AccountNotLinked: '200002',
    /**
     * A linked org has no resolvable OWNER `OrganizationMember` — shouldn't
     * happen (provisioning always creates one), so this is a data-integrity
     * guard, not a Saleshandy-specific condition, same class as
     * `Billing.Shared.LimitNotConfigured`. Currently only reachable from
     * `SaleshandyLinkService.updateMailboxWarmupStatus`, which needs an
     * actor identity to call through to `EmailAccountsService.pauseWarmup`/
     * `resumeWarmup`.
     */
    OwnerNotResolved: '200003',
  },
  /**
   * POST /saleshandy/accounts/link (link=01) — the push path — plus
   * POST /saleshandy/link/verify (also link=01: same logical action,
   * human-initiated instead of pushed), the reverse retroactive-linking flow
   * an already-logged-in TI user drives by pasting their own Saleshandy API
   * key. See `SaleshandyLinkVerificationController` /
   * `SaleshandyLinkService.retroactiveLink`.
   */
  Link: {
    InvalidOwnerPayload: '200101',
    /** Saleshandy rejected the pasted API key, or the outbound verify call failed/is unconfigured. */
    InvalidApiKey: '200102',
    /** This TI organization already has a different Saleshandy account linked — `UNIQUE(organizationId)`. */
    OrganizationAlreadyLinked: '200103',
    /** This Saleshandy account is already linked to a different TI organization — `UNIQUE(shAccountId)`. */
    ShAccountAlreadyLinked: '200104',
  },
  /**
   * POST /saleshandy/subscriptions/sync (sync=04). Also reused by the
   * `/link` provisioning path (`SaleshandyProvisioningService`), which
   * resolves the SAME `saleshandyPlanMappings` row for its initial plan —
   * one condition, one code, regardless of which endpoint hits the miss.
   *
   * Deliberately a hard failure, never a silent `free` fallback (repo hard
   * rule: never substitute a value for a lookup that didn't resolve).
   */
  Sync: {
    PlanMappingNotFound: '200401',
  },
  /**
   * POST /saleshandy/accounts/:shAccountId/email-infra/verify (email-infra=05,
   * §8f) — the OAuth-code relay for a Saleshandy-purchased Email-Infra
   * mailbox. See docs/plans/saleshandy-account-sync.md §8f.
   */
  EmailInfra: {
    /** The Stage 0 OAuth app credential (a config/secrets handoff, not yet provisioned) isn't set for this method. */
    OAuthNotConfigured: '200501',
    /** `SaleshandyApiService.verifyEmailWebhook` returned `isValid: false`, errored, or is unconfigured. */
    OrderVerificationFailed: '200502',
    /** `type` wasn't Gmail(1) or Microsoft(2) — SMTP/IMAP mailboxes never need OAuth token exchange (legacy's own condition). */
    InvalidMethod: '200503',
  },
} as const;

export const SALESHANDY_CATALOG: Record<string, CatalogEntry> = {
  '200001': {
    httpStatus: 401,
    message: 'Invalid or missing Saleshandy API token.',
  },
  '200002': {
    httpStatus: 404,
    message: 'This Saleshandy account is not linked to a TrulyInbox organization.',
  },
  '200003': {
    httpStatus: 500,
    message: 'Could not resolve an owner for this organization.',
  },
  '200101': {
    httpStatus: 400,
    message: 'The linked account payload is missing or malformed.',
  },
  '200102': {
    httpStatus: 400,
    message: 'This Saleshandy API key could not be verified.',
  },
  '200103': {
    httpStatus: 409,
    message: 'This organization already has a Saleshandy account linked.',
  },
  '200104': {
    httpStatus: 409,
    message: 'This Saleshandy account is already linked to a different organization.',
  },
  '200401': {
    httpStatus: 404,
    message: 'This Saleshandy plan is not mapped to a TrulyInbox plan.',
  },
  '200501': {
    httpStatus: 503,
    message: 'Email-Infra mailbox connection is not available yet.',
  },
  '200502': {
    httpStatus: 400,
    message: 'This mailbox order could not be verified.',
  },
  '200503': {
    httpStatus: 400,
    message: 'This mailbox type does not support Email-Infra OAuth connection.',
  },
};
